import { useMutation, useQueryClient } from "@tanstack/react-query";

import { getTouchpointVersionFeedback } from "generated/api/touchpoint-version-feedback";
import type {
  TouchpointVersionDTOAiFeedbackRating,
  TouchpointVersionFeedbackRequest,
} from "generated/schemas";

import { touchpointVersionKeys } from "hooks/api/query-keys";
import {
  setTouchpointVersionCache,
  type TouchpointVersionAttributes,
} from "hooks/api/use-touchpoint-versions";
import type { PaginatedResponse } from "types/pagination";

const feedbackApi = getTouchpointVersionFeedback();

export type TouchpointVersionFeedbackScope = {
  clientId: string;
  campaignId: string;
  touchpointId: string;
  versionId: string;
};

export const useSubmitTouchpointVersionFeedback = (scope: TouchpointVersionFeedbackScope) => {
  const queryClient = useQueryClient();
  const { clientId, campaignId, touchpointId, versionId } = scope;

  return useMutation({
    mutationFn: (request: TouchpointVersionFeedbackRequest) =>
      feedbackApi.submitFeedback({ clientId, campaignId, touchpointId, versionId }, request),
    onSuccess: (_data, request) => {
      const versionScope = { clientId, campaignId, touchpointId };
      const detailKey = touchpointVersionKeys.detail(
        clientId,
        campaignId,
        touchpointId,
        versionId,
      );
      const currentVersion = queryClient.getQueryData<TouchpointVersionAttributes>(detailKey);

      if (currentVersion) {
        setTouchpointVersionCache(queryClient, versionScope, {
          ...currentVersion,
          aiFeedbackRating: request.type,
        });
      } else {
        queryClient.setQueriesData<PaginatedResponse<TouchpointVersionAttributes>>(
          {
            queryKey: touchpointVersionKeys.listScope(clientId, campaignId, touchpointId),
          },
          (currentData) => {
            if (!currentData) {
              return currentData;
            }

            return {
              ...currentData,
              items: currentData.items.map((version) =>
                version.id === versionId
                  ? { ...version, aiFeedbackRating: request.type }
                  : version,
              ),
            };
          },
        );
      }

      queryClient.invalidateQueries({
        queryKey: touchpointVersionKeys.listScope(clientId, campaignId, touchpointId),
      });
    },
  });
};

export function hasTouchpointVersionFeedback(
  aiFeedbackRating: TouchpointVersionDTOAiFeedbackRating | null | undefined,
): boolean {
  return Boolean(aiFeedbackRating);
}
